import React, { useState } from 'react';
import { motion } from 'framer-motion';

const FEMININE = [
  { from: 'Emotionaler Abhängigkeit', to: 'unerschütterlicher innerer Stabilität' },
  { from: 'Kontrolle, Sehnsucht & Verlustangst', to: 'tiefem Vertrauen und Hingabe' },
  { from: 'Bestätigung im Außen', to: 'starkem Selbstwert – sich selbst genügen' },
  { from: 'Bedürftigkeit', to: 'radikaler Selbstliebe und Selbstfürsorge' },
  { from: 'Liebe beweisen müssen', to: 'Liebe einfach sein' },
  { from: 'Fokus auf den Anderen', to: 'Fokus zurück zu dir selbst' },
];

const MASCULINE = [
  { from: 'Emotionaler Verdrängung', to: 'Gefühle zulassen und ausdrücken' },
  { from: 'Kontrolle & Rückzug', to: 'Vertrauen und echte Nähe zulassen' },
  { from: 'Angst vor Verletzlichkeit', to: 'Herzöffnung und Authentizität' },
  { from: 'Selbstschutz', to: 'tiefem Vertrauen ins Leben und in die Liebe' },
  { from: 'Verantwortung vermeiden', to: 'absolute Verantwortung für eigene Gefühle' },
  { from: 'Freiheit vs. Bindung', to: 'Balance und bewusste Entscheidungen' },
];

const GlowItem = ({ item, index, color }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, x: color === 'gold' ? -20 : 20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-30px' }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="flex items-start gap-3 py-3 px-4 cursor-default transition-all duration-500 rounded-sm"
      style={{
        background: hovered
          ? color === 'gold' ? 'rgba(212,175,55,0.06)' : 'rgba(192,192,192,0.05)'
          : 'transparent',
        borderLeft: `2px solid ${hovered
          ? color === 'gold' ? '#D4AF37' : '#C0C0C0'
          : 'rgba(255,255,255,0.06)'}`,
        boxShadow: hovered
          ? color === 'gold' ? '-3px 0 20px rgba(212,175,55,0.2)' : '-3px 0 20px rgba(192,192,192,0.1)'
          : 'none',
        transition: 'all 0.5s ease',
      }}>
      <motion.div
        animate={hovered ? {
          scale: 1.4,
          boxShadow: color === 'gold' ? '0 0 14px rgba(212,175,55,0.9)' : '0 0 10px rgba(192,192,192,0.6)'
        } : { scale: 1, boxShadow: 'none' }}
        transition={{ duration: 0.4 }}
        className="w-1.5 h-1.5 rounded-full mt-2 shrink-0"
        style={{ background: color === 'gold' ? '#D4AF37' : '#C0C0C0' }}
      />
      <p className="text-sm font-light leading-relaxed"
        style={{
          color: hovered
            ? color === 'gold' ? 'rgba(212,175,55,0.95)' : 'rgba(240,240,240,0.9)'
            : 'rgba(249,249,249,0.5)',
          transition: 'color 0.5s ease',
        }}>
        <span className="line-through opacity-50">{item.from}</span>
        <span className="mx-2 opacity-40">→</span>
        <span className="font-serif italic">{item.to}</span>
      </p>
    </motion.div>
  );
};

const DualityMap = () => (
  <section className="relative py-28 bg-darker border-t border-white/5 overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,rgba(212,175,55,0.03)_0%,transparent_70%)]" />

    <div className="relative z-10 max-w-6xl mx-auto px-6">
      <div className="text-center mb-20">
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          className="text-gold tracking-[0.35em] uppercase text-xs mb-6 font-semibold">
          Die Duality Map
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.9 }}
          className="text-4xl md:text-6xl font-serif text-cream leading-tight">
          Dein Weg der{' '}
          <span className="italic text-transparent bg-clip-text bg-gold-gradient">Transformation</span>
        </motion.h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Feminine Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.9 }}
          className="relative p-8 md:p-10 rounded-sm overflow-hidden"
          style={{
            background: 'rgba(212,175,55,0.03)',
            border: '1px solid rgba(212,175,55,0.15)',
            boxShadow: '0 0 60px rgba(212,175,55,0.05)',
          }}>
          <div className="absolute top-0 left-0 right-0 h-[1px]"
            style={{ background: 'linear-gradient(to right, transparent, rgba(212,175,55,0.5), transparent)' }} />
          <div className="absolute top-0 right-0 w-32 h-32 bg-gold/5 blur-[40px] rounded-full pointer-events-none" />
          <div className="mb-8">
            <p className="text-gold tracking-[0.25em] uppercase text-xs mb-2 font-semibold">Weibliche Evolution</p>
            <h3 className="text-2xl font-serif text-cream">Dein Weg</h3>
            <p className="text-cream/40 text-sm mt-2 font-light">Von ... zu ...</p>
          </div>
          <div className="space-y-1">
            {FEMININE.map((item, i) => (
              <GlowItem key={i} item={item} index={i} color="gold" />
            ))}
          </div>
          <motion.p
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
            viewport={{ once: true }} transition={{ delay: 0.8 }}
            className="mt-8 text-base font-serif italic text-gold/80 border-t border-gold/15 pt-6">
            "Liebe nicht mehr beweisen müssen – sondern sie sein."
          </motion.p>
        </motion.div>

        {/* Masculine Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.2 }}
          className="relative p-8 md:p-10 rounded-sm overflow-hidden"
          style={{
            background: 'rgba(192,192,192,0.02)',
            border: '1px solid rgba(192,192,192,0.1)',
            boxShadow: '0 0 60px rgba(192,192,192,0.03)',
          }}>
          <div className="absolute top-0 left-0 right-0 h-[1px]"
            style={{ background: 'linear-gradient(to right, transparent, rgba(192,192,192,0.3), transparent)' }} />
          <div className="absolute top-0 left-0 w-32 h-32 bg-white/3 blur-[40px] rounded-full pointer-events-none" />
          <div className="mb-8">
            <p className="text-silver tracking-[0.25em] uppercase text-xs mb-2 font-semibold">Der männliche Spiegel</p>
            <h3 className="text-2xl font-serif text-cream">Sein Weg</h3>
            <p className="text-cream/40 text-sm mt-2 font-light">Von ... zu ...</p>
          </div>
          <div className="space-y-1">
            {MASCULINE.map((item, i) => (
              <GlowItem key={i} item={item} index={i} color="silver" />
            ))}
          </div>
          <motion.p
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
            viewport={{ once: true }} transition={{ delay: 0.8 }}
            className="mt-8 text-base font-serif italic text-cream/50 border-t border-white/10 pt-6">
            "Bewusste Entscheidungen treffen und in die Führung gehen."
          </motion.p>
        </motion.div>
      </div>
    </div>
  </section>
);

export default DualityMap;